import React, { useState, useEffect } from 'react';
import { ButtonBasic, NoDataContent } from "@lk-gtcom/ecomlab-components";
import customHeaders, { getSpace } from "../../common/headers";
import { BreadcrumbBasic } from '../../components/BreadcrumbBasic/BreadcrumbBasic';
import useGeneralStore from "../../store/general";
import { useShallow } from "zustand/react/shallow";
import { TabsGreen } from "../../components/TabsGreen/TabsGreen";
import { ButtonActionDrop } from "../../components/Buttons/ButtonActionDrop/ButtonActionDrop";
import { TitlePage } from '../../components/TitlePage/TitlePage';
import { SceletonSpaceCard } from './SceletonSpaceCard/SceletonSpaceCard';
import { ModalCreatingNewSpace } from '../../components/Modal/ModalCreatingNewSpace/ModalCreatingNewSpace';
import { ButtonWithIcon } from '../../components/Buttons/ButtonWithIcon/ButtonWithIcon';
import { useResponseHandler } from '../../common/auth';
import './SpacePage.scss';

// Импорт изображений
import userIcon from './img/bx-user.svg';
import userCircleIcon from './img/bx-user-circle.svg';
import groupIcon from './img/bx-group.svg';
import pinIcon from './img/bx-pin.svg';
import starIcon from './img/bxs-star.svg';
import useModal from '../../store/modal';

// Типы данных
interface SpaceData {
  id: string;
  name: string;
  is_admin: boolean;
  author?: { id: string };
  employees?: any[];
  is_sticky: boolean,
  is_favorite: boolean
}

interface TabButton {
  label: string;
  count: number;
  value: string;
  icon: string;
}

interface Counter {
  total: number;
  admin: number;
  member: number;
}

interface CardSpaceProps {
  id: number | string,
  spaceData: SpaceData;
  setIsEditSpace: (value: boolean) => void;
  setIsModal: (value: boolean) => void;
  deleteFunc: (id: string) => void;
  refreshFetch: () => void;
  setCurrentSpace: (space: SpaceData) => void;
  fetchGetInfoSpace: (id: string | number) => void;
  fetchSetFavorite: (space_id: string | number, status: boolean) => void
  fetchSetPin: (space_id: string | number, status: boolean) => void
}

interface SpacePageProps {
  setIsModalCreatingNewSpace: (value: boolean) => void;
  isEditSpace: boolean
  setIsEditSpace: (value: boolean) => void;
  isModalCreatingNewSpace: boolean;
  fetchGetSpace: () => void;
  spaceList: SpaceData[];
  setIsModalActive: (value: boolean) => void;
  setConnectedModal: (value: boolean) => void;
  setIsModalRole: (value: boolean) => void;
}

const client_id = localStorage.getItem('client_id');

const CardSpace: React.FC<CardSpaceProps> = ({
  id,
  spaceData,
  setIsEditSpace,
  setIsModal,
  deleteFunc,
  refreshFetch,
  setCurrentSpace,
  fetchSetFavorite,
  fetchSetPin
}) => {
  const { setPageValue1 } = useGeneralStore();

  const is_admin = spaceData?.is_admin;
  const btns = [
    ...(spaceData.is_favorite
      ? [{ btn: 'Убрать из избранного', func: () => fetchSetFavorite(id, false) }]
      : [{ btn: 'Добавить в избранное', func: () => fetchSetFavorite(id, true) }]),
    ...(spaceData.is_sticky
      ? [{ btn: 'Открепить', func: () => fetchSetPin(id, false) }]
      : [{ btn: 'Закрепить', func: () => fetchSetPin(id, true) }]),
    ...(is_admin
      ? [{ btn: 'Редактировать', func: () => handleCardClick() },
      {
        btn: 'Удалить', func: () => {
          deleteFunc(spaceData.id);
          setTimeout(() => refreshFetch(), 500);
        }
      }] : []),
  ];

  const handleCardClick = () => {
    setCurrentSpace(spaceData);
    setIsEditSpace(true);
    setPageValue1('employee');
    setIsModal(true);
  };

  const handleButtonClick = (pageValue: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSpace(spaceData);
    setIsEditSpace(true);
    setPageValue1(pageValue);
    setIsModal(true);
  };

  return (
    <div className='card-space'
      style={{ border: spaceData.is_sticky ? '1px solid #22C55E' : '' }}
      onClick={handleCardClick}>
      <div className='card-space__header'>
        <div className='card-space__header-panel'>
          {is_admin ? (
            <div className="card-space__admin-badge">
              <img src={userCircleIcon} alt="Админ" /> Админ
            </div>
          ) : (
            <div className="card-space__user-badge">
              <img src={userIcon} alt="Сотрудник" /> Сотрудник
            </div>
          )}

          {spaceData.is_favorite && <img
            width={16}
            height={16}
            src={starIcon} alt="Избранное" />}

          {spaceData.is_sticky && <img
            width={16}
            height={16}
            src={pinIcon} alt="Закрепить" />}

          <ButtonActionDrop
            id={spaceData.id}
            btns={btns}
            size={'s'}
          />

        </div>
        <h3 className='card-space__title'>{spaceData.name}</h3>
      </div>
      <div className='card-space__content'>
        <button className="card-space__btn" onClick={handleButtonClick('employee')}>
          Сотрудники
        </button>
        <button className="card-space__btn" onClick={handleButtonClick('settings')}>
          Подключения
        </button>
        <button className="card-space__btn" onClick={handleButtonClick('roles')}>
          Роли
        </button>
        {is_admin && (
          <button className="card-space__btn" onClick={handleButtonClick('get_token')}>
            Получить токен
          </button>
        )}
      </div>
    </div>
  );
};

const SpacePage: React.FC<SpacePageProps> = ({
  setIsModalCreatingNewSpace,
  isEditSpace,
  setIsEditSpace,
  isModalCreatingNewSpace,
  fetchGetSpace,
  setIsModalActive,
  setConnectedModal
}) => {

  const {
    currentSpace,
    setCurrentSpace,
    selectedSpace,
    setSelectedSpace
  } = useGeneralStore(
    useShallow((state) => ({
      currentSpace: state.currentSpace,
      setCurrentSpace: state.setCurrentSpace,
      selectedSpace: state.selectedSpace,
      setSelectedSpace: state.setSelectedSpace,
    }))
  );

  const { isModalActive, } = useModal(
    useShallow((state) => ({
      isModalActive: state.isModalActive
    }))
  );
  const headers = { ...customHeaders, ...getSpace(selectedSpace) };
  const { handleTableResponse, handleRequestError } = useResponseHandler();

  const [isFavorite, setIsFavorite] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [load, setLoad] = useState(false)
  const [spaceList, setSpaceList] = useState([])
  const [count, setCount] = useState<Counter>({
    total: 0,
    admin: 0,
    member: 0
  })

  const currentSpaceList = (): SpaceData[] => {
    if (!status) return spaceList;

    switch (status) {
      case 'admin':
        return spaceList.filter(({ author }) => author?.id == client_id);
      case 'employee':
        return spaceList.filter(({ author }) => author?.id != client_id);
      default:
        return spaceList;
    }
  };

  const btns: TabButton[] = [
    { label: 'Все', count: count?.total, value: 'all', icon: groupIcon },
    { label: 'Админ', count: count?.admin, value: 'admin', icon: userCircleIcon },
    { label: 'Сотрудник', count: count?.member, value: 'member', icon: userIcon }
  ];

  useEffect(() => {
    setStatus(btns[0]?.value);
  }, []);

  const fetchDeleteSpace = (id: string) => {
    const url = `https://apps0.ecomru.ru:4242/api/v2/space/${id}`;

    fetch(url, { method: 'DELETE', headers })
      .then(handleTableResponse)
      .then((json) => {
        setTimeout(() => {
          fetchGetSpace();
          fetchGetSpaceList()
          fetchGetCounter()
        }, 300)
      })
      .catch(handleRequestError);
  };

  // Каунтер для табов
  const fetchGetCounter = () => {
    const url = `https://apps0.ecomru.ru:4242/api/v2/space/counter${isFavorite ? '?is_favorite=true' : ''}`;

    fetch(url, { method: 'GET', headers })
      .then(handleTableResponse)
      .then((json) => setCount(json))
      .catch(handleRequestError);
  };

  // Получение пространств
  const fetchGetSpaceList = () => {
    const abortController = new AbortController();
    const url = `https://apps0.ecomru.ru:4242/api/v2/space/list?role=${status}${isFavorite ? '&is_favorite=true' : ''}`;
    setLoad(true)

    fetch(url, { headers, signal: abortController.signal })
      .then(handleTableResponse)
      .then((json) => setSpaceList(json?.data || []))
      .catch(handleRequestError)
      .finally(() => setLoad(false))
    return abortController;
  };

  // Получение информации о пространстве
  const fetchGetInfoSpace = (id: string | number) => {
    const url = `https://apps0.ecomru.ru:4242/api/v2/space/${id}`;

    fetch(url, { method: 'GET', headers })
      .then(handleTableResponse)
      .then((json) => setCurrentSpace(json))
      .catch(handleRequestError);
  };

  // Добавление в избранное
  const fetchSetFavorite = (space_id: string | number, status: boolean) => {
    const url = `https://apps0.ecomru.ru:4242/api/v2/space/${space_id}/favorite`;

    const body = JSON.stringify({
      is_favorite: status
    })

    fetch(url, { method: 'PATCH', headers, body })
      .then(handleTableResponse)
      .then((json) => setTimeout(() => fetchGetSpaceList(), 500))
      .catch(handleRequestError);
  };

  // Добавление в избранное
  const fetchSetPin = (space_id: string | number, status: boolean) => {
    const url = `https://apps0.ecomru.ru:4242/api/v2/space/${space_id}/sticky`;

    const body = JSON.stringify({
      is_sticky: status
    })

    fetch(url, { method: 'PATCH', headers, body })
      .then(handleTableResponse)
      .then((json) => setTimeout(() => fetchGetSpaceList(), 500))
      .catch(handleRequestError);
  };

  useEffect(() => {
    fetchGetCounter()
  }, [spaceList, isFavorite]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchGetCounter()
      fetchGetSpace();
      fetchGetSpaceList()
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [isModalCreatingNewSpace, selectedSpace, status, isFavorite]);

  const list = [
    {
      label: 'Пространства',
      link: 'Croshka_ 1',
      func: () => {
        setIsModalCreatingNewSpace(false)
      }
    },
    ...(currentSpace?.name
      ? [
        {
          label: currentSpace?.name
        }
      ]
      : []),
    ...((!currentSpace?.name)
      ? [
        {
          label: 'Новое пространство'
        }
      ]
      : []),
  ]

  return (
    <>
      {/* Создание нового пространства */}

      {isModalCreatingNewSpace && <BreadcrumbBasic
        list={list}
      />}

      {
        isModalCreatingNewSpace ?
          <ModalCreatingNewSpace
            spaceListCurrent={spaceList}
            setIsModal={setIsModalCreatingNewSpace}
            isEdit={isEditSpace}
            setIsEditSpace={setIsEditSpace}
            isModalActive={isModalActive}
            setIsModalActive={setIsModalActive}
            setSelectedSpace={setSelectedSpace}
            setConnectedModal={setConnectedModal}
            fetchSetFavorite={fetchSetFavorite}
            fetchSetPin={fetchSetPin}
            fetchGetSpace={fetchGetSpace}
          />
          :
          <div className='space-page'>
            <TitlePage
              title='Пространства'
            />

            <div className='space-page__toolbar'>
              <ButtonBasic
                green
                text='Создать пространство'
                width='210px'
                onClick={() => setIsModalCreatingNewSpace(true)}
              />

              <ButtonWithIcon
                favorite
                whiteGreen
                size='m'
                changeValue={isFavorite}
                onClick={(e) => setIsFavorite(!isFavorite)}
              />
            </div>

            <TabsGreen
              btns={btns}
              currentBtn={status || ''}
              setCurrentBtn={(value: string) => setStatus(value)}
              checkValue={true}
              returnEvent={() => { }}
              icon={null}
            />

            <div className='space-page__content'>
              {load ?
                [...Array(8)].map((el, ind) => {
                  return <SceletonSpaceCard
                    key={el}
                  />
                })
                :
                currentSpaceList()?.length > 0 ?
                  currentSpaceList()?.map((el, index) => (
                    <CardSpace
                      id={el.id}
                      key={`${el.id}-${index}`}
                      spaceData={el}
                      setCurrentSpace={setCurrentSpace}
                      setIsEditSpace={setIsEditSpace}
                      setIsModal={setIsModalCreatingNewSpace}
                      deleteFunc={fetchDeleteSpace}
                      refreshFetch={fetchGetSpace}
                      fetchGetInfoSpace={fetchGetInfoSpace}
                      fetchSetFavorite={fetchSetFavorite}
                      fetchSetPin={fetchSetPin}
                    />)
                  )
                  :
                  <NoDataContent
                    title="Нет доступных пространств"
                    description="Создайте новое пространство, чтобы пользоваться всеми инструментами сервиса"
                  />
                }
            </div>
          </div>
      }
    </>);
};

export { SpacePage };